import React from "react";
import Select, { SingleValue } from "react-select";
import { DateInput } from "./DateInput";
import { PasswordToggleInput } from "./PasswordToggleInput";
import styles from "./Input.module.scss";
import { IInputProps, SelectOption } from "shared/interfaces";
import { Landmark } from "lucide-react";

type NativeInputProps = React.InputHTMLAttributes<HTMLInputElement>;

function resolveRegister(
  register: any,
  name?: string,
  rules?: any
) {
  if (!register) return {};

  // 👉 quando vier assim: register("cpf", {...})
  if (typeof register === "object") {
    return register;
  }

  // 👉 quando vier assim: register
  if (typeof register === "function" && name) {
    return register(name, rules);
  }

  return {};
}

export function Input({
  type = "text",
  name,
  label,
  placeholder,
  value,
  onChange,
  options = [],
  disabled,
  customClass,
  icon,
  register,
  rules,
  error,
  ...rest
}: IInputProps & NativeInputProps & { icon?: React.ReactNode }) {
  /* ================================
   SELECT COM LOGO
================================ */
if (type === "select-with-logo") {
  const customOptions = options.map((opt: SelectOption) => ({
    value: opt.value,
    label: opt.label,
    logo: opt.logo,
  }));

    return (
      <div className={`${styles.inputContainer} ${customClass || ""}`}>
        {label && <label htmlFor={name}>{label}</label>}

        <Select
          classNamePrefix="customSelect"
          placeholder={placeholder}
          options={customOptions}
          value={customOptions.find((opt) => opt.value === value) || null}
          isDisabled={disabled}
          isSearchable
          formatOptionLabel={(option) => (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              {option.logo ? (
              <img src={option.logo} alt={option.label} width={24} height={24}
                style={{borderRadius: 4, objectFit: "contain",}}/> ) : (
              <Landmark size={24} />)}
              <span>{option.label}</span>
            </div>
          )}
          onChange={(selected: SingleValue<any>) =>
            onChange?.({
              target: {
                name,
                value: selected?.value ?? "",
              },
            })
          }
        />

        {error && <p className={styles.error}>{error}</p>}
      </div>
    );
    }

  /* ================================
     SELECT PADRÃO
  ================================ */
  if (type === "select") {
    return (
      <div className={styles.inputContainer}>
        {label && <label htmlFor={name}>{label}</label>}

        <div className={`${styles.selectWrapper} ${customClass || ""}`}>
          <select
            {...resolveRegister(register, name, rules)}
            name={name}
            id={name}
            value={value}
            onChange={onChange}
            disabled={disabled}
            className={styles.inputs}
          >
            <option value="">{placeholder}</option>
            {options.map((opt: SelectOption) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          {icon && <div className={styles.selectIcon}>{icon}</div>}
        </div>

        {error && <p className={styles.error}>{error}</p>}
      </div>
    );
  }

  /* ================================
     TEXTAREA
  ================================ */
  if (type === "textarea") {
    return (
      <div className={styles.inputContainer}>
        {label && <label htmlFor={name}>{label}</label>}
        <textarea
          {...resolveRegister(register, name, rules)}
          {...(rest as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
          name={name}
          id={name}
          placeholder={placeholder}
          disabled={disabled}
          className={`${styles.inputs} ${customClass || ""}`}
        />
        {error && <p className={styles.error}>{error}</p>}
      </div>
    );
  }

  /* ================================
     INPUT DE DATA
  ================================ */
  if (type === "date") {
    return (
      <div className={styles.inputContainer}>
        {label && <label htmlFor={name}>{label}</label>}
        <div className={styles.dateWrapperModern}>
          <DateInput
            {...resolveRegister(register, name, rules)}
            {...rest}
            name={name}
            placeholder={placeholder}
            disabled={disabled}
            className={`${styles.inputs} ${customClass || ""}`}
          />
          {icon && <div className={styles.dateIconModern}>{icon}</div>}
        </div>
        {error && <p className={styles.error}>{error}</p>}
      </div>
    );
  }

  /* ================================
     PASSWORD COM TOGGLE
  ================================ */
  if (type === "password-toggle") {
    return (
      <PasswordToggleInput
        name={name}
        label={label}
        placeholder={placeholder}
        register={register}
        rules={rules}
        error={error}
        disabled={disabled}
        customClass={customClass}
        {...rest}
      />
    );
  }

  /* ================================
     INPUT PADRÃO
  ================================ */
  return (
    <div className={styles.inputContainer}>
      {label && <label htmlFor={name}>{label}</label>}
      <div className={icon ? styles.icon : ""}>
        {icon && <span className="inputIcon">{icon}</span>}
        <input
          {...resolveRegister(register, name, rules)}
          {...rest}
          type={type}
          name={name}
          id={name}
          placeholder={placeholder}
          disabled={disabled}
          className={`${styles.inputs} ${customClass || ""}`}
        />
      </div>
      {error && <p className={styles.error}>{error}</p>}
    </div>
  );
}
